import React, { useEffect, useState, useCallback } from "react";
import { Download, X, Share } from "lucide-react";
import { useLocation } from "react-router-dom";

const DISMISS_KEY = "gfn_install_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const InstallPWABanner = () => {
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const { pathname } = useLocation();
  const appName = pathname.startsWith("/on-demand") ? "GFN Soul On Demand" : "GFN Soul NYC";

  const shouldShow = useCallback(() => {
    if (isStandalone()) return false;
    const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (last && Date.now() - last < DISMISS_TTL_MS) return false;
    return true;
  }, []);

  useEffect(() => {
    if (!shouldShow()) return;

    const onAvail = () => setVisible(true);
    const onDone = () => setVisible(false);
    window.addEventListener("gfn-install-available", onAvail);
    window.addEventListener("gfn-install-done", onDone);

    // If we already captured the prompt before this component mounted
    if (window.__gfnDeferredInstall) setVisible(true);

    // iOS Safari never fires beforeinstallprompt — show a hint after a short delay
    if (isIOS()) {
      const t = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("gfn-install-available", onAvail);
        window.removeEventListener("gfn-install-done", onDone);
      };
    }

    return () => {
      window.removeEventListener("gfn-install-available", onAvail);
      window.removeEventListener("gfn-install-done", onDone);
    };
  }, [shouldShow]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    const evt = window.__gfnDeferredInstall;
    if (!evt) return;
    evt.prompt();
    try {
      await evt.userChoice;
    } catch (_) {
      /* ignore */
    }
    window.__gfnDeferredInstall = null;
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed left-1/2 -translate-x-1/2 z-[70] bottom-24 md:bottom-6 w-[92vw] max-w-md rounded-lg border border-gold/40 bg-black/95 backdrop-blur-xl shadow-[0_0_40px_rgba(0,0,0,0.7)] px-4 py-3 flex items-center gap-3"
    >
      <div className="w-10 h-10 grid place-items-center rounded border-gold-gradient shrink-0">
        <Download className="w-4 h-4 text-gold" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-condensed uppercase tracking-widest text-[10px] text-gold">
          Install {appName}
        </div>
        {iosHint ? (
          <div className="text-xs text-neutral-300 mt-0.5 flex items-center gap-1 flex-wrap">
            Tap <Share className="w-3.5 h-3.5 inline text-gold" /> then
            <span className="text-gold">Add to Home Screen</span>
          </div>
        ) : (
          <div className="text-xs text-neutral-300 mt-0.5">
            Add to home screen · Full-screen · No browser bar
          </div>
        )}
      </div>
      {!iosHint && (
        <button
          data-testid="pwa-install-btn"
          onClick={install}
          className="bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-[10px] px-3 py-2 rounded-full transition-colors shrink-0"
        >
          Install
        </button>
      )}
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-neutral-500 hover:text-gold shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default InstallPWABanner;
